import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Lock, User, LogIn, ArrowLeft, Shield, Briefcase } from 'lucide-react';
import { toast } from 'sonner';
import { siteInfo } from '../data/mock';
import { api } from '../api/client';

/**
 * Pannello di accesso unificato, parametrizzato per ruolo.
 * - mode="admin"           => richiede ruolo 'admin'
 * - mode="organizzazione"  => richiede ruolo 'organizzazione'
 * Nessun suggerimento di credenziali è mostrato in UI.
 */
export default function AdminLogin({ mode = 'admin' }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const isOrg = mode === 'organizzazione';
  const requiredRole = isOrg ? 'organizzazione' : 'admin';
  const portalLabel = isOrg ? 'Area Organizzazione' : 'Area Amministratore';
  const portalHint = isOrg
    ? 'Accesso riservato allo staff per la gestione di risultati e classifiche.'
    : 'Accesso riservato all\'amministratore del sito.';
  const PortalIcon = isOrg ? Briefcase : Shield;
  const loginPath = isOrg ? '/organizzazione' : '/admin';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await api.login(username, password);
      if (res.role !== requiredRole) {
        toast.error(`Queste credenziali non sono valide per ${portalLabel}.`);
        setSubmitting(false);
        return;
      }
      localStorage.setItem('adminAuth', JSON.stringify({
        token: res.token, role: res.role, label: res.label,
      }));
      toast.success(`Accesso ${res.label}`);
      navigate('/admin/dashboard');
    } catch (err) {
      toast.error(err?.message || 'Credenziali non valide o backend non raggiungibile');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-navy flex items-center justify-center p-4 relative overflow-hidden">
      <div className="absolute inset-0 z-0">
        <img src="https://images.unsplash.com/photo-1508098682722-e99c43a406b2?w=1600&q=80" alt="" className="w-full h-full object-cover opacity-15" />
        <div className="absolute inset-0 bg-gradient-to-br from-navy-dark/95 to-navy/90"></div>
      </div>

      <div className="relative z-10 w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 text-white/80 hover:text-pink-brand mb-6 text-sm font-semibold" data-testid="back-to-site-link">
          <ArrowLeft size={16} /> Torna al sito
        </Link>

        <div className="glass-card p-8" data-testid={`login-card-${requiredRole}`}>
          <div className="text-center mb-7">
            <img src={siteInfo.logo} alt="logo" className="w-20 h-20 mx-auto object-contain mb-4" />
            <div className="inline-flex items-center gap-2 bg-pink-brand/15 border border-pink-brand/40 px-3 py-1 rounded-full mb-3">
              <PortalIcon size={14} className="text-pink-brand" />
              <span className="text-pink-brand text-[10px] font-bold uppercase tracking-widest">{portalLabel}</span>
            </div>
            <h1 className="font-brand text-2xl font-bold text-white">Area Riservata</h1>
            <p className="text-white/60 text-sm mt-2">{portalHint}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-white text-sm font-semibold mb-2" htmlFor="login-username">Username</label>
              <div className="relative">
                <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                <input
                  id="login-username"
                  data-testid="login-username-input"
                  type="text"
                  autoComplete="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full bg-navy-dark border border-white/20 rounded-lg pl-11 pr-4 py-3 text-white focus:outline-none focus:border-pink-brand transition-colors"
                />
              </div>
            </div>
            <div>
              <label className="block text-white text-sm font-semibold mb-2" htmlFor="login-password">Password</label>
              <div className="relative">
                <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                <input
                  id="login-password"
                  data-testid="login-password-input"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-navy-dark border border-white/20 rounded-lg pl-11 pr-4 py-3 text-white focus:outline-none focus:border-pink-brand transition-colors"
                  placeholder="••••••••"
                />
              </div>
            </div>
            <button type="submit" disabled={submitting} data-testid="login-submit-button" className="btn-primary w-full justify-center disabled:opacity-60">
              <LogIn size={18} /> {submitting ? 'Accesso in corso…' : 'Accedi'}
            </button>
          </form>

          <div className="mt-6 pt-5 border-t border-white/10 text-center">
            {isOrg ? (
              <Link to="/admin" className="text-white/50 hover:text-pink-brand text-xs font-semibold" data-testid="switch-to-admin-link">
                Sei un amministratore? Accedi qui
              </Link>
            ) : (
              <Link to="/organizzazione" className="text-white/50 hover:text-pink-brand text-xs font-semibold" data-testid="switch-to-org-link">
                Sei dello staff Organizzazione? Accedi qui
              </Link>
            )}
          </div>

          {/* Path utilizzato per logout (debug-only, non visibile) */}
          <input type="hidden" value={loginPath} readOnly />
        </div>
      </div>
    </div>
  );
}
